import React from 'react'
import {Authenticator} from '@aws-amplify/ui-react'
import {
  signUp,
  signIn,
  resetPassword,
  confirmResetPassword
} from 'aws-amplify/auth'
import {getLogger} from '../../services/logging'
import {pushDataLayerEvent} from '../../services/dataLayer'

const logger = getLogger('MemberAuthenticator')

// -----------------------------------------------------------------------------
//  Authenticator Form Fields
// -----------------------------------------------------------------------------
const formFields = {
  signIn: {
    username: {
      label: 'Username (email)',
      labelHidden: false,
      placeholder: 'Enter your email',
      order: 1
    },
    password: {
      label: 'Password',
      labelHidden: false,
      placeholder: 'Enter your password',
      order: 2
    }
  },
  signUp: {
    username: {
      label: 'Username (email)',
      labelHidden: false,
      placeholder: 'Enter your email',
      order: 1
    },
    given_name: {
      label: 'First Name',
      labelHidden: false,
      placeholder: 'Enter your first name',
      order: 2
    },
    family_name: {
      label: 'Last Name',
      labelHidden: false,
      placeholder: 'Enter your last name',
      order: 3
    },
    phone_number: {
      label: 'Phone (Mobile)',
      labelHidden: false,
      placeholder: 'Enter your mobile phone number',
      order: 4
    },
    password: {
      label: 'Password',
      labelHidden: false,
      placeholder: 'Enter your password',
      order: 5
    },
    confirm_password: {
      label: 'Confirm Password',
      labelHidden: false,
      placeholder: 'Confirm your password',
      order: 6
    }
  },
  forceNewPassword: {
    password: {
      label: 'Password',
      labelHidden: false,
      placeholder: 'Enter your Password'
    }
  },
  resetPassword: {
    username: {
      label: 'Username (email)',
      labelHidden: false,
      placeholder: 'Enter your email'
    }
  },
  confirmResetPassword: {
    confirmation_code: {
      label: 'Confirmation Code',
      labelHidden: false,
      placeholder: 'Enter your confirmation code',
      order: 1
    },
    password: {
      label: 'Password',
      labelHidden: false,
      placeholder: 'Enter your password',
      order: 2
    },
    confirm_password: {
      labelHidden: false,
      placeholder: 'Enter your password',
      order: 3
    }
  },
  confirmSignIn: {
    confirmation_code: {
      label: 'Confirmation Code',
      labelHidden: false,
      placeholder: 'Enter your confirmation code'
    }
  }
}

// -----------------------------------------------------------------------------
//  Authenticator Service Functions
// -----------------------------------------------------------------------------
const services = {
  async handleSignUp(input: any) {
    // convert email/username to lowercase
    const lowerUsername = input.username.toLowerCase()

    logger.info({
      message: 'Attempting to register new user...',
      data: {username: lowerUsername}
    })

    pushDataLayerEvent({
      event: 'User_Register',
      category: 'IBE',
      value: 1
    })

    return signUp({
      username: lowerUsername,
      password: input.password,
      options: {
        ...input.options,
        autoSignIn: true
      }
    })
  },

  async handleSignIn(input: any) {
    // convert username to lowercase
    const lowerUsername = input.username.toLowerCase()

    logger.info({
      message: 'Attempting to sign in user...',
      data: {username: lowerUsername}
    })

    pushDataLayerEvent({
      event: 'User_Login',
      category: 'IBE',
      value: 1
    })

    return signIn({
      username: lowerUsername,
      password: input.password
    })
  },

  async handleForgotPassword(input: any) {
    // convert username to lowercase before requesting a reset code
    const lowerUsername = input.username.toLowerCase()

    logger.info({
      message: 'Attempting to send password reset code...',
      data: {username: lowerUsername}
    })

    return resetPassword({username: lowerUsername})
  },

  async handleForgotPasswordSubmit(input: any) {
    // keep username lowercase so it matches the reset request above
    const {username, newPassword, confirmationCode} = input

    return confirmResetPassword({
      username: username.toLowerCase(),
      newPassword,
      confirmationCode
    })
  }
}

export enum MemberAuthenticatorView {
  SignIn = 'signIn',
  SignUp = 'signUp'
}

export interface MemberAuthenticatorProps {
  className?: string
  view?: MemberAuthenticatorView
}

const MemberAuthenticator = ({
  className = '',
  view = MemberAuthenticatorView.SignIn
}: MemberAuthenticatorProps) => {
  return (
    <div className={className}>
      <Authenticator
        initialState={view}
        services={services}
        formFields={formFields}
      />
    </div>
  )
}

export default MemberAuthenticator
